import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon, SearchIcon, MarketMapIcon } from './icons.jsx'
import { EMPTY_GEO, geoCount, geoSummary } from '../lib/geo.js'

// Location picker for the leads directory. A chip button opens a popover with a
// "By state / By city" toggle and a checkbox list. In city mode, cities are
// grouped under collapsible state headers — check a state to grab all its
// cities, or expand it to pick individual cities ("city in a state").

function useClickOutside(ref, onAway) {
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) onAway()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [ref, onAway])
}

function toggleIn(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

function Check({ checked, indeterminate, onChange }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate) && !checked
  }, [indeterminate, checked])
  return (
    <input
      ref={ref}
      type="checkbox"
      className="h-[15px] w-[15px] flex-none cursor-pointer accent-primary"
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
    />
  )
}

export default function GeoPicker({ index, value, onChange, disabled }) {
  const geo = { ...EMPTY_GEO, ...value }
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(() => new Set())
  const ref = useRef(null)
  useClickOutside(ref, () => setOpen(false))

  const q = query.trim().toLowerCase()
  const count = geoCount(geo)

  const setMode = (mode) => onChange({ ...geo, mode })

  // --- State mode rows ---
  const stateRows = useMemo(
    () => index.filter((s) => !q || s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)),
    [index, q],
  )

  // --- City mode groups (filter cities by query; auto-expand matches) ---
  const cityGroups = useMemo(() => {
    if (!q) return index.map((s) => ({ ...s, matchCities: s.cities }))
    return index
      .map((s) => {
        const stateHit = s.name.toLowerCase().includes(q)
        const matchCities = stateHit ? s.cities : s.cities.filter((c) => c.city.toLowerCase().includes(q))
        return { ...s, matchCities }
      })
      .filter((s) => s.matchCities.length)
  }, [index, q])

  const isExpanded = (code) => q !== '' || expanded.has(code)

  function toggleExpand(code) {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(code) ? next.delete(code) : next.add(code)
      return next
    })
  }

  function toggleStateCities(state) {
    const keys = state.cities.map((c) => c.key)
    const allOn = keys.every((k) => geo.cities.includes(k))
    const set = new Set(geo.cities)
    keys.forEach((k) => (allOn ? set.delete(k) : set.add(k)))
    onChange({ ...geo, cities: [...set] })
  }

  function stateCityStatus(state) {
    const keys = state.cities.map((c) => c.key)
    const on = keys.filter((k) => geo.cities.includes(k)).length
    return { checked: on === keys.length && keys.length > 0, some: on > 0 }
  }

  function clearAll() {
    onChange({ ...geo, states: [], cities: [] })
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex min-w-[220px] max-w-[360px] items-center gap-x-2 rounded-[9px] border border-solid px-3 py-[9px] text-[14px] font-medium transition-colors ${
          count > 0 ? 'border-primary text-ink' : 'border-line text-ink hover:bg-panel'
        } ${disabled ? 'cursor-not-allowed opacity-60' : 'bg-surface'}`}
      >
        <MarketMapIcon className="h-4 w-4 flex-none text-faint" />
        <span className="min-w-0 flex-1 truncate text-left">{geoSummary(geo)}</span>
        {count > 0 && (
          <span className="flex-none rounded-full bg-primary-tint px-[7px] py-px text-[11px] font-semibold text-primary-dark">
            {count}
          </span>
        )}
        <ChevronDownIcon className="h-4 w-4 flex-none text-faint" />
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-40 w-[340px] rounded-[12px] border border-line bg-surface shadow-[0_12px_32px_rgba(15,23,42,0.18)]">
          {/* Mode toggle */}
          <div className="flex items-center gap-x-1 p-2.5 [border-bottom:1px_solid_var(--border)]">
            <div className="relative flex flex-1 rounded-[9px] bg-panel p-[3px]">
              {/* Sliding highlight — translates between the two options instead of snapping. */}
              <span
                aria-hidden="true"
                className="absolute inset-y-[3px] left-[3px] w-[calc(50%-3px)] rounded-[7px] bg-surface shadow-card transition-transform duration-200 ease-out"
                style={{ transform: geo.mode === 'city' ? 'translateX(100%)' : 'translateX(0)' }}
              />
              {[
                ['state', 'By state'],
                ['city', 'By city'],
              ].map(([m, label]) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`relative z-[1] flex-1 rounded-[7px] px-3 py-[6px] text-[13px] font-semibold transition-colors ${
                    geo.mode === m ? 'text-ink' : 'text-dim hover:text-ink'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {count > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="rounded-[7px] px-2.5 py-[6px] text-[12px] font-semibold text-dim hover:bg-panel hover:text-ink"
              >
                Clear
              </button>
            )}
          </div>

          {/* Search */}
          <div className="p-2.5 pb-1.5">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-[10px] top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-faint" />
              <input
                type="search"
                autoFocus
                className="w-full rounded-[8px] border border-line border-solid bg-panel py-[7px] pl-[30px] pr-2 text-[13px] text-ink focus:border-primary focus:outline-none"
                placeholder={geo.mode === 'state' ? 'Search states…' : 'Search cities…'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-[320px] overflow-y-auto px-1.5 pb-2">
            {geo.mode === 'state'
              ? stateRows.map((s) => (
                  <label
                    key={s.code}
                    className="flex cursor-pointer items-center gap-x-2.5 rounded-[8px] px-2 py-[7px] hover:bg-panel"
                  >
                    <Check
                      checked={geo.states.includes(s.code)}
                      onChange={() => onChange({ ...geo, states: toggleIn(geo.states, s.code) })}
                    />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{s.name}</span>
                    <span className="text-[12px] tabular-nums text-faint">{s.count.toLocaleString()}</span>
                  </label>
                ))
              : cityGroups.map((s) => {
                  const status = stateCityStatus(s)
                  const exp = isExpanded(s.code)
                  return (
                    <div key={s.code}>
                      <div className="flex items-center gap-x-2 rounded-[8px] px-2 py-[7px] hover:bg-panel">
                        <Check
                          checked={status.checked}
                          indeterminate={status.some}
                          onChange={() => toggleStateCities(s)}
                        />
                        <button
                          type="button"
                          onClick={() => toggleExpand(s.code)}
                          className="flex min-w-0 flex-1 items-center gap-x-1.5 bg-transparent text-left"
                        >
                          <ChevronDownIcon
                            className={`h-[14px] w-[14px] flex-none text-faint transition-transform ${exp ? '' : '-rotate-90'}`}
                          />
                          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">{s.name}</span>
                          <span className="text-[12px] tabular-nums text-faint">
                            {(s.matchCities || s.cities).length} cities
                          </span>
                        </button>
                      </div>
                      {exp && (
                        <div className="mb-1 ml-[26px] [border-left:1px_solid_var(--table-line)] pl-2">
                          {(s.matchCities || s.cities).map((c) => (
                            <label
                              key={c.key}
                              className="flex cursor-pointer items-center gap-x-2.5 rounded-[8px] px-2 py-[6px] hover:bg-panel"
                            >
                              <Check
                                checked={geo.cities.includes(c.key)}
                                onChange={() => onChange({ ...geo, cities: toggleIn(geo.cities, c.key) })}
                              />
                              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{c.city}</span>
                              <span className="text-[12px] tabular-nums text-faint">{c.count.toLocaleString()}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
            {((geo.mode === 'state' && !stateRows.length) || (geo.mode === 'city' && !cityGroups.length)) && (
              <div className="px-2 py-4 text-center text-[13px] text-faint">No matches.</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
